import { TileCoord } from './types';

/**
 * Fetches map tiles from a tile server with caching and progress tracking.
 */
export class TileFetcher {
    private cache: Map<string, HTMLImageElement> = new Map();
    private maxConcurrent: number;
    private subdomains: string[];

    constructor(maxConcurrent: number = 6, subdomains: string[] = ['a', 'b', 'c']) {
        this.maxConcurrent = maxConcurrent;
        this.subdomains = subdomains;
    }

    /**
     * Fetch all tiles for the given coordinates.
     *
     * @param tiles - Array of tile coordinates to fetch
     * @param urlTemplate - URL template with {s}, {z}, {x}, {y} placeholders
     * @param onProgress - Optional callback for progress updates
     * @returns Map of tile keys to loaded images
     */
    async fetchTiles(
        tiles: TileCoord[],
        urlTemplate: string,
        onProgress?: (loaded: number, total: number) => void
    ): Promise<Map<string, HTMLImageElement>> {
        const results = new Map<string, HTMLImageElement>();
        let loaded = 0;
        const total = tiles.length;

        // Filter out already cached tiles
        const tilesToFetch: TileCoord[] = [];
        for (const tile of tiles) {
            const key = this.getTileKey(tile);
            if (this.cache.has(key)) {
                results.set(key, this.cache.get(key)!);
                loaded++;
            } else {
                tilesToFetch.push(tile);
            }
        }

        // Report initial progress (cached tiles)
        onProgress?.(loaded, total);

        // Create a counter object to track progress (avoids closure issues)
        const progress = { loaded };

        // Helper to process a single tile
        const processTile = async (tile: TileCoord): Promise<void> => {
            try {
                const img = await this.fetchTile(tile, urlTemplate);
                const key = this.getTileKey(tile);
                this.cache.set(key, img);
                results.set(key, img);
            } catch (error) {
                console.warn(`Failed to load tile ${this.getTileKey(tile)}:`, error);
            }
            progress.loaded++;
            onProgress?.(progress.loaded, total);
        };

        // Process tiles in batches with concurrency limit
        const fetchQueue = [...tilesToFetch];

        while (fetchQueue.length > 0) {
            const batch = fetchQueue.splice(0, this.maxConcurrent);
            await Promise.all(batch.map(processTile));
        }

        return results;
    }

    /**
     * Fetch a single tile.
     */
    private async fetchTile(tile: TileCoord, urlTemplate: string): Promise<HTMLImageElement> {
        const url = this.buildTileUrl(tile, urlTemplate);

        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous'; // Required for canvas export

            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Failed to load tile: ${url}`));

            img.src = url;
        });
    }

    /**
     * Build the tile URL from the template and tile coordinates.
     */
    private buildTileUrl(tile: TileCoord, urlTemplate: string): string {
        const subdomain = this.subdomains[Math.floor(Math.random() * this.subdomains.length)];

        return urlTemplate
            .replace('{s}', subdomain)
            .replace('{z}', tile.z.toString())
            .replace('{x}', tile.x.toString())
            .replace('{y}', tile.y.toString());
    }

    /**
     * Get a unique key for a tile coordinate.
     */
    private getTileKey(tile: TileCoord): string {
        return `${tile.z}/${tile.x}/${tile.y}`;
    }

    /**
     * Clear the tile cache.
     */
    clearCache(): void {
        this.cache.clear();
    }

    /**
     * Get the number of cached tiles.
     */
    getCacheSize(): number {
        return this.cache.size;
    }
}
